<template>
  <div>
    <provisioning-status @refresh="load" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>サイト一覧</span>
        <manual-button path="/site-list/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-row no-gutters>
          <v-col>
            <app-search :search="site.search" v-model="site.filter" />
          </v-col>
          <div class="d-flex flex-row ga-1 flex-wrap ml-2">
            <v-btn v-permission.config color="primary" @click="registerSite"
              >追加</v-btn
            >
            <v-btn v-permission.config color="primary" @click="deleteSite"
              >削除</v-btn
            >
            <v-btn color="primary" @click="downloadCsv">一覧ダウンロード</v-btn>
          </div>
        </v-row>
        <v-alert v-if="alertMsg" class="my-2" :text="alertMsg"></v-alert>
        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="load">一覧更新</v-btn>
        </div>
        <app-table
          v-model:table-sort-by="site.sortBy"
          primary-key="siteId"
          :selectable="selectable"
          :fields="site.fields"
          :items="site.items"
          :filterOption="site.filter_option"
          :filter="site.filter"
          v-model="site.selected"
          :hover="true"
          sticky-header="50vh"
          @click:row="siteDetails"
        >
        </app-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import SiteReference from "@/modals/site/SiteReference.vue";
import SiteEdit from "@/modals/site/SiteEdit.vue";
import type {
  Type1InternetAddress,
  Type1PrivateSiteDelete,
  Type1PrivateSiteDeleteLine,
  Type1PrivateLine,
  Type1PrivateSite,
} from "@/apis/Type1SiteApi";
import { mapState } from "vuex";
import { defineComponent } from "vue";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type { SearchOption } from "@/components/AppSearch.vue";

export default defineComponent({
  name: "SiteList",
  data() {
    return {
      isLoaded: false,
      alertMsg: null as string | null,
      site: {
        sortBy: { key: "enumber", order: "desc" } as SortItem,
        fields: [
          {
            key: "enumber",
            title: "回線番号",
            sortable: true,
            width: "200px",
          },
          {
            key: "vlan",
            title: "VLAN",
            sortable: true,
            width: "200px",
            align: "end",
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (value: string, key: null, item: SiteListEntity) => {
              if (item.vlanIdType === "SPECIFIED") {
                return `${item.vlanId}`;
              } else {
                return this.$filter.enumTo(item.vlanIdType, "vlanIdType");
              }
            },
          },
          {
            key: "siteName",
            title: "サイト名",
            sortable: true,
            width: "320px",
          },
        ] as AppTableHeader<SiteListEntity>[],
        items: [] as SiteListEntity[],
        selected: [] as SiteListEntity[],
        search: {
          enumber: { type: "text", label: "回線番号" },
          vlan: { type: "text", label: "VLAN" },
          siteName: { type: "text", label: "サイト名" },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          enumber: "like",
          vlan: "like",
          siteName: "like",
        } as FilterOption,
      },
      type1SiteZoneKeyId: "",
    };
  },
  computed: {
    ...mapState("user", {
      contractSummary: "contractSummary",
    }),
    /** true: 一覧選択可能, false: 不可 */
    selectable(): boolean {
      return this.$service.permission.hasAuthority("CONFIG");
    },
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    /** 一覧を取得 */
    async load() {
      this.alertMsg = null;
      /** Type1プライベートサイトゾーン取得API */
      const type1PrivateList =
        await this.$api.type1SiteZone.getType1PrivateList();
      this.type1SiteZoneKeyId = type1PrivateList.type1SiteZoneKeyId;
      this.site.items = type1PrivateList.lineList.flatMap(
        (line: Type1PrivateLine) =>
          line.siteList.map((e: Type1PrivateSite) => ({
            enumber: line.enumber,
            accessType: line.accessType,
            vlanIdType: e.vlanIdType,
            vlanId: e.vlanId,
            siteName: e.siteName,
            siteId: e.siteId,
            addressList: e.addressList,
          }))
      );
    },

    /** サイト詳細表示 */
    async siteDetails(item: SiteListEntity) {
      this.alertMsg = null;
      await this.$modal.show(SiteReference, {
        siteData: item,
        type1SiteZoneKeyId: this.type1SiteZoneKeyId,
      });
      await this.load();
    },

    /** サイト作成 */
    async registerSite() {
      this.alertMsg = null;
      await this.$modal.show(SiteEdit, {
        type1SiteZoneKeyId: this.type1SiteZoneKeyId,
      });
      await this.load();
    },

    /** サイト削除 */
    async deleteSite() {
      if (this.site.selected.length > 0) {
        if (
          this.contractSummary.isLargeScaleContract &&
          this.site.selected.length > 1
        ) {
          // 大規模契約ユーザでは一度に複数の行の削除はできません
          this.alertMsg = this.$filter.message("more_than_one_delete");
          return;
        }
        this.alertMsg = null;
        /** 確認ダイアログの表示 */
        await this.$confirm(`サイトを削除します。よろしいですか？`);

        /** APIリクエストデータのマッピング処理 */
        const lineList = [] as Type1PrivateSiteDeleteLine[];
        this.site.selected.map((e) => {
          if (lineList.find((i) => i.enumber === e.enumber)) {
            const idx = lineList.findIndex((i) => i.enumber === e.enumber);
            lineList[idx].siteIdList.push(e.siteId);
          } else {
            lineList.push({
              enumber: e.enumber,
              siteIdList: [e.siteId],
            });
          }
        });
        const deletedData: Type1PrivateSiteDelete = {
          lineList: lineList,
          type1SiteZoneKeyId: this.type1SiteZoneKeyId,
        };
        /** APIによるサイト削除処理 */
        await this.$api.type1SiteZone.postType1PrivateSiteDelete(deletedData);
        await this.load();
      } else {
        this.alertMsg = this.$filter.message("no_select_data");
      }
    },
    /** フロント側のみでのCSVダウンロード */
    downloadCsv() {
      this.$service.csvService.appTableCsvDownLoad(
        "site_setting",
        this.site.items.sortBy(["enumber", "desc"]),
        this.$service.csvService.genFieldList(this.site.fields)
      );
    },
  },
});

/** 一覧画面データマッピング用インタフェース */
export interface SiteListEntity {
  /** 回線番号 */
  enumber: string;
  /**VLAN IDのType指定*/
  vlanIdType: "UNTAG" | "OTHER" | "SPECIFIED";
  /**VLAN ID*/
  vlanId: number | null;
  /** サイト名 */
  siteName: string;
  /** サイトID（サイト情報変更のパスパラメータ） */
  siteId: string;
  /** フロー情報設定時使用する情報 */
  accessType:
    | "ETHERNET_L2"
    | "ETHERNET_L3"
    | "BROADBAND_ACCESS2_L2"
    | "BROADBAND_ACCESS2_L3"
    | "WIRELESS_ACCESS_L2"
    | "WIRELESS_ACCESS_L3"
    | "REMOTE_ACCESS_GW_FRE"
    | "REMOTE_ACCESS_GW_CPA"
    | "REMOTE_ACCESS_GW_CRG"
    | "PLATFORM_GATEWAY"
    | "PF_ACCESS_GW"
    | "EXTEND_ETHERNET"
    | "IPSEC"
    | "ETHERNET2"
    | "ETHERNET2_PF"
    | "BROADBAND_ACCESS3"
    | "WIRELESS_ACCESS2"
    | "REMOTE_ACCESS_GW2_FRE"
    | "REMOTE_ACCESS_GW2_CPA"
    | "REMOTE_ACCESS_GW2_CRG"
    | "PF_ACCESS_GW2"
    | "ETHERNET_LIGHT";
  addressList: Type1InternetAddress[];
}
</script>
